import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Loader2,
    LogOut,
    CalendarDays,
    DollarSign,
    Star,
    Inbox,
    MapPin,
    Phone,
    Mail,
    Lock,
} from "lucide-react";
import { API, SERVICES, BUSINESS } from "@/lib/site";
import axios from "axios";

const TOKEN_KEY = "nextgen_admin_token";
const serviceName = (id) => SERVICES.find((s) => s.id === id)?.title || id;

function formatDetail(detail) {
    if (!detail) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    return "Login failed.";
}

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchSummary = useCallback(async (t) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/summary`, {
                headers: { Authorization: `Bearer ${t}` },
            });
            setData(data);
        } catch (e) {
            if (e.response?.status === 401) {
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
                setData(null);
                toast.error("Session expired — please log in again.");
            } else {
                toast.error("Could not load dashboard.");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (token) fetchSummary(token);
    }, [token, fetchSummary]);

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setData(null);
    };

    if (!token) return <LoginGate onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;

    return (
        <main data-testid="admin-dashboard" className="bg-sand min-h-[100svh] pt-[74px]">
            <section className="mx-auto max-w-7xl px-5 md:px-10 py-12 md:py-16">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <p className="text-sm uppercase tracking-[0.25em] text-forest/50 mb-2">Owner dashboard</p>
                        <h1 className="font-heading font-extrabold tracking-tight text-forest text-4xl md:text-5xl">
                            Welcome back, Ben.
                        </h1>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="admin-logout"
                        className="inline-flex items-center gap-2 rounded-full border border-forest/20 text-forest px-5 py-2.5 text-sm font-semibold hover:bg-forest hover:text-sand transition-colors"
                    >
                        <LogOut size={16} /> Sign out
                    </button>
                </div>

                {loading && !data ? (
                    <div className="flex items-center gap-2 text-forest/60 mt-16">
                        <Loader2 className="animate-spin" size={18} /> Loading…
                    </div>
                ) : data ? (
                    <>
                        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <Stat icon={Inbox} label="Total bookings" value={data.stats.total_bookings} testid="stat-total-bookings" />
                            <Stat icon={CalendarDays} label="Bookings this month" value={data.stats.bookings_this_month} testid="stat-month-bookings" />
                            <Stat icon={DollarSign} label="Total revenue" value={`$${data.stats.total_revenue}`} featured testid="stat-revenue" />
                            <Stat icon={Star} label="Avg rating" value={`${data.stats.avg_rating} ★`} testid="stat-rating" />
                        </div>

                        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Bookings */}
                            <div className="lg:col-span-2">
                                <h2 className="font-heading font-bold text-2xl text-forest mb-5">
                                    Bookings ({data.bookings.length})
                                </h2>
                                {data.bookings.length === 0 ? (
                                    <EmptyState text="No bookings yet. They'll appear here the moment a customer books." />
                                ) : (
                                    <div className="space-y-4" data-testid="bookings-list">
                                        {data.bookings.map((b, i) => (
                                            <motion.div
                                                key={b.id}
                                                initial={{ opacity: 0, y: 12 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: Math.min(i * 0.03, 0.3) }}
                                                className="rounded-2xl bg-white border border-black/5 p-5"
                                            >
                                                <div className="flex flex-wrap items-start justify-between gap-3">
                                                    <div>
                                                        <p className="font-heading font-bold text-lg text-forest">{b.name}</p>
                                                        <p className="text-sm text-forest/60 flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                                                            <span className="flex items-center gap-1"><Phone size={13} /> {b.phone}</span>
                                                            <span className="flex items-center gap-1"><Mail size={13} /> {b.email}</span>
                                                        </p>
                                                        <p className="text-sm text-forest/60 flex items-center gap-1 mt-1">
                                                            <MapPin size={13} /> {b.address}
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="font-heading font-extrabold text-2xl text-forest">${b.total}</p>
                                                        <p className="text-xs text-forest/50">{b.preferred_date} · {b.preferred_time}</p>
                                                    </div>
                                                </div>
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {b.services.map((s) => (
                                                        <span key={s} className="rounded-full bg-sage/50 text-forest text-xs px-3 py-1">
                                                            {serviceName(s)}
                                                        </span>
                                                    ))}
                                                </div>
                                                {b.notes ? <p className="mt-3 text-sm text-forest/55 italic">"{b.notes}"</p> : null}
                                            </motion.div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Reviews */}
                            <div>
                                <h2 className="font-heading font-bold text-2xl text-forest mb-5">
                                    Reviews ({data.reviews.length})
                                </h2>
                                {data.reviews.length === 0 ? (
                                    <EmptyState text="No customer-submitted reviews yet." />
                                ) : (
                                    <div className="space-y-4" data-testid="reviews-list">
                                        {data.reviews.map((r) => (
                                            <div key={r.id} className="rounded-2xl bg-white border border-black/5 p-5">
                                                <div className="flex items-center justify-between">
                                                    <p className="font-semibold text-forest">{r.name}</p>
                                                    <span className="text-forest text-sm">{"★".repeat(r.rating)}<span className="text-forest/20">{"★".repeat(5 - r.rating)}</span></span>
                                                </div>
                                                {r.location ? <p className="text-xs text-forest/45">{r.location}</p> : null}
                                                <p className="mt-2 text-sm text-forest/70">{r.text}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                ) : null}
            </section>
        </main>
    );
}

function Stat({ icon: Icon, label, value, featured, testid }) {
    return (
        <div
            data-testid={testid}
            className={`rounded-2xl p-5 border ${featured ? "bg-forest text-sand border-forest" : "bg-white text-forest border-black/5"}`}
        >
            <Icon size={20} className={featured ? "text-sky" : "text-forest/40"} />
            <p className="font-heading font-extrabold text-3xl mt-4">{value}</p>
            <p className={`text-xs mt-1 ${featured ? "text-sand/70" : "text-forest/50"}`}>{label}</p>
        </div>
    );
}

function EmptyState({ text }) {
    return (
        <div className="rounded-2xl border border-dashed border-forest/20 p-8 text-center text-forest/50 text-sm">
            {text}
        </div>
    );
}

function LoginGate({ onLogin }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            toast.success("Welcome back!");
            onLogin(data.access_token);
        } catch (err) {
            toast.error(formatDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <main data-testid="admin-login" className="bg-forest text-sand min-h-[100svh] grain flex items-center pt-[74px]">
            <div className="relative z-10 mx-auto w-full max-w-md px-5 py-16">
                <span className="inline-flex h-14 w-14 rounded-2xl bg-sky text-forest items-center justify-center mb-6">
                    <Lock size={24} />
                </span>
                <h1 className="font-heading font-extrabold tracking-tight text-4xl">Owner login</h1>
                <p className="text-sand/70 mt-2">Sign in to view bookings, reviews, and stats.</p>
                <form onSubmit={submit} className="mt-8 space-y-4">
                    <input
                        data-testid="login-email"
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full rounded-xl bg-sand/10 border border-sand/20 px-4 py-3 text-sand placeholder:text-sand/40 outline-none focus:border-sky transition-colors"
                    />
                    <input
                        data-testid="login-password"
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full rounded-xl bg-sand/10 border border-sand/20 px-4 py-3 text-sand placeholder:text-sand/40 outline-none focus:border-sky transition-colors"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="login-submit"
                        className="w-full rounded-full bg-sand text-forest py-4 font-semibold hover:bg-sky transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                        {loading && <Loader2 className="animate-spin" size={18} />}
                        {loading ? "Signing in…" : "Sign in"}
                    </button>
                </form>
                <p className="mt-6 text-center text-xs text-sand/40">{BUSINESS.name} · Owner access only</p>
            </div>
        </main>
    );
}
